import { useEffect, useState } from "react";
import { signInWithCustomToken } from "firebase/auth";
import { useHistory, useLocation } from "react-router-dom";
import { z } from "zod";
import { auth } from "../../services/firebase";
import { markScreenshotSession } from "../../domains/app/screenshotSession";

declare global {
  interface Window {
    __screenshotToken: string | undefined;
  }
}

const redirectSchema = z.object({
  redirect: z.string().min(1),
});

type Status =
  | { step: "waiting-for-token" }
  | { step: "authenticating" }
  | { step: "redirecting" }
  | { step: "error"; message: string };

function extractPath(redirect: string): string {
  try {
    const url = new URL(redirect);
    return url.pathname + url.search + url.hash;
  } catch {
    return redirect;
  }
}

export default function ScreenshotAuthPage(): React.JSX.Element {
  const location = useLocation();
  const history = useHistory();
  const [status, setStatus] = useState<Status>({ step: "waiting-for-token" });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const parsed = redirectSchema.safeParse({
      redirect: params.get("redirect"),
    });

    if (!parsed.success) {
      setStatus({
        step: "error",
        message: "Missing required query parameter: redirect",
      });
      return;
    }

    const { redirect } = parsed.data;

    function authenticate(token: string): void {
      setStatus({ step: "authenticating" });
      signInWithCustomToken(auth, token)
        .then(() => {
          // Flag the screenshot session only AFTER auth succeeds (a valid
          // minted token), so a tokenless visitor to /screenshot-auth can't set
          // it and suppress room-entry gates. Synchronous sessionStorage write,
          // so it's present before the redirected room route evaluates it.
          markScreenshotSession();
          setStatus({ step: "redirecting" });
          history.replace(extractPath(redirect));
        })
        .catch((error: unknown) => {
          const message =
            error instanceof Error
              ? error.message
              : "Unknown authentication error";
          setStatus({ step: "error", message });
        });
    }

    const injectedToken = window.__screenshotToken;
    if (injectedToken !== undefined) {
      authenticate(injectedToken);
      return;
    }

    const handler = (): void => {
      const token = window.__screenshotToken;
      if (token !== undefined) {
        authenticate(token);
      }
    };
    window.addEventListener("screenshotTokenReady", handler);
    return () => {
      window.removeEventListener("screenshotTokenReady", handler);
    };
  }, [location.search, history]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      {status.step === "error" ? (
        <p style={{ color: "red" }}>{status.message}</p>
      ) : (
        <p>
          {status.step === "waiting-for-token"
            ? "Waiting for token..."
            : status.step === "authenticating"
              ? "Authenticating..."
              : "Redirecting..."}
        </p>
      )}
    </div>
  );
}
